/**
 * Preview-only mirror of the backend slug pipeline (`hg/hg/slugs.py`).
 *
 * This exists so the settings forms can show the resulting URL as the user
 * types. The backend value is authoritative and is echoed back on save, so if
 * the two ever drift the user sees a corrected value rather than a broken URL.
 *
 * Keep the steps below in the same order as the Python version.
 */

export const MIN_NICKNAME_LENGTH = 4;
export const MAX_NICKNAME_LENGTH = 30;
export const MAX_COLLECTION_SLUG_LENGTH = 80;
export const MAX_RECORD_SLUG_LENGTH = 80;

/** The separator segment in record URLs: /{user}/-/{record-slug}. */
export const RECORD_SEPARATOR = "-";

// Cf/Cc/Co/Cs. Cf matters most: it holds the bidi overrides (U+202E and
// friends) and zero-width joiners, which can make a slug render as something
// other than what it is.
const INVISIBLE = /[\p{Cf}\p{Cc}\p{Co}\p{Cs}]/gu;

// Intra-word punctuation, dropped rather than turned into a separator so
// "don't" reads as "dont" and not "don-t".
const QUOTES = /['‘’`"“”]/gu;

// Letters, numbers, all mark categories, and "-". Marks must include Mc:
// Devanagari vowel signs are spacing marks, and allowing only Mn mangles
// "हिन्दी" into "ह-न्द".
const NOT_SLUG = /[^\p{L}\p{N}\p{M}-]+/gu;

const ASCII_SLUG = /^[a-z0-9-]+$/;

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  let cut = value.slice(0, maxLength);
  const boundary = cut.lastIndexOf("-");
  if (boundary >= Math.floor(maxLength / 2)) {
    cut = cut.slice(0, boundary);
  }
  return trimHyphens(cut);
}

function trimHyphens(value: string): string {
  return value.replace(/^-+/, "").replace(/-+$/, "");
}

/** Normalize arbitrary text into a URL name. May return "". */
export function normalizeSlug(text: string, maxLength: number): string {
  if (!text) return "";

  let value = text.normalize("NFC");
  value = value.replace(INVISIBLE, "");
  value = value.toLowerCase();
  value = value.replace(QUOTES, "");
  value = value.replace(NOT_SLUG, "-");
  value = value.replace(/-{2,}/g, "-");
  value = trimHyphens(value);
  value = truncate(value, maxLength);

  // A slug of pure marks is possible ("❤️" reduces to a lone variation
  // selector) and is worse than empty: it renders as an invisible URL.
  if (!/[\p{L}\p{N}]/u.test(value)) return "";

  return value;
}

export type NicknameProblem =
  | "empty"
  | "too_short"
  | "non_ascii"
  | null;

/**
 * Client-side nickname check. Unlike collection/record slugs this reports
 * non-ASCII rather than stripping it: a nickname is an identity, and silently
 * turning "Сергей" into something else is worse than an explicit error.
 *
 * Reserved-word and uniqueness checks live on the server only.
 */
export function checkNickname(value: string): {
  normalized: string;
  problem: NicknameProblem;
} {
  const normalized = normalizeSlug(value, MAX_NICKNAME_LENGTH);
  if (!normalized) return { normalized, problem: "empty" };
  if (!ASCII_SLUG.test(normalized)) return { normalized, problem: "non_ascii" };
  if (normalized.length < MIN_NICKNAME_LENGTH)
    return { normalized, problem: "too_short" };
  return { normalized, problem: null };
}

export const NICKNAME_MESSAGES: Record<string, string> = {
  empty: "Nickname must contain letters or digits.",
  too_short: `Nickname must be at least ${MIN_NICKNAME_LENGTH} characters long.`,
  non_ascii: "Nickname may only use latin letters a-z, digits and hyphens.",
  reserved: "That nickname is reserved.",
  taken: "That nickname is already taken.",
};
